// Privacy + Terms copy. Shared by the React page and scripts/prerender.js
// (which reads this file via regex — keep it plain data, no imports).
export const LEGAL_PAGES = {
  privacy: {
    path: `/privacy`,
    metaTitle: `Privacy Policy | Pawground`,
    metaDescription: `How Pawground collects, uses and protects your information, the services we use, and how to delete your account.`,
    heading: `Pawground Privacy Policy`,
    effective: `Effective date: October 2026`,
    blocks: [
      { p: `Pawground ("we", "us") runs the website pawground.app and the Pawground mobile app. This policy explains what information we collect, how we use it, and your choices. Questions: info@pawground.app.` },
      { h2: `Information you give us` },
      { ul: [
        `Account info: your name, email address and password (stored encrypted). If you sign in with Google on our website, we receive your name, email and profile picture from Google.`,
        `Your dogs: names, breed, size and other details you add to your dog profiles.`,
        `Things you post: reviews, ratings, check-ins and notes, "how busy is it" reports, places you suggest or confirm as dog-friendly, and saved favorites. Reviews and check-ins are shown publicly with your name and your dog's name.`,
        `Photos: profile and dog photos you upload. These may be shown with your reviews and check-ins.`,
        `Messages: feedback, reports and emails you send us.`,
        `Private park bookings: if you request a booking, the name, email, phone number and message you provide, which we share with the host.`,
      ] },
      { h2: `Information collected automatically` },
      { ul: [
        `Location: if you allow it, we use your device's location to show places near you. We use it only while you are using the app or site, and we do not keep a history of your location.`,
        `Usage data: basic information like pages viewed, device and browser type, and general activity, used to keep the service working and improve it.`,
      ] },
      { h2: `How we use your information` },
      { ul: [
        `To run Pawground: show places, save your favorites, post your reviews and check-ins, and keep you logged in.`,
        `To send account emails such as password resets.`,
        `To respond to your messages and reports.`,
        `To keep the community safe, including reviewing reported content.`,
        `To understand how Pawground is used and improve it.`,
      ] },
      { p: `We do not sell your personal information.` },
      { h2: `Services we use` },
      { p: `We use trusted providers to operate Pawground, and they process information only to provide their service to us:` },
      { ul: [
        `Emergent (app hosting and database)`,
        `Google (place information, Google sign-in on our website, and Google Analytics on our website)`,
        `Meta (Meta Pixel on our website, to measure our ads)`,
        `Microsoft Clarity (usage analytics and session recordings on our website)`,
        `PostHog (usage analytics and session recordings on our website)`,
        `Resend (sending emails)`,
        `OpenStreetMap and Apple Maps (maps and looking up your city from your location)`,
        `Open-Meteo (weather; no personal information is shared)`,
      ] },
      { h2: `Your choices` },
      { ul: [
        `You can edit or remove your dogs, favorites and posts in your profile.`,
        `You can turn off location access in your device settings at any time.`,
        `Delete your account: in the app, go to Profile > Delete account, or email info@pawground.app. When you delete your account, we delete your account, dogs, check-ins, favorites and personal details, and remove your photos so they can no longer be seen or accessed. Reviews you wrote stay on the place page but are shown as "Former Pawground member" with your name and dog's name removed.`,
      ] },
      { h2: `Children` },
      { p: `Pawground is not intended for children under 13, and we do not knowingly collect information from children under 13.` },
      { h2: `Security` },
      { p: `We use reasonable measures to protect your information, but no service is completely secure.` },
      { h2: `Changes` },
      { p: `We may update this policy. We will change the effective date above and, for important changes, let you know in the app or by email.` },
      { h2: `Contact` },
      { p: `info@pawground.app` },
    ],
  },
  terms: {
    path: `/terms`,
    metaTitle: `Terms of Service | Pawground`,
    metaDescription: `The terms for using the Pawground website and app, including community rules, place information, private parks and account deletion.`,
    heading: `Pawground Terms of Service`,
    effective: `Effective date: October 2026`,
    blocks: [
      { p: `By using the Pawground website or app, you agree to these terms. If you don't agree, please don't use Pawground.` },
      { h2: `Using Pawground` },
      { ul: [
        `Pawground helps you find dog-friendly and pet-friendly places. You must be at least 13 years old to create an account.`,
        `Keep your password safe. You are responsible for activity on your account.`,
      ] },
      { h2: `Place information` },
      { p: `Place details (hours, pet policies, amenities and more) come from public sources and from our community, and may be out of date or wrong. Always check with the business or park before you go, follow posted rules and local leash laws, and supervise your pet. You visit places at your own risk.` },
      { h2: `Your content` },
      { ul: [
        `You own the reviews, photos, check-ins and other content you post. By posting, you allow Pawground to display, store and share that content as part of the service.`,
        `Only post content that is honest and based on your own experience.`,
      ] },
      { h2: `Community rules — zero tolerance` },
      { p: `Pawground has no tolerance for objectionable content or abusive users. Do not post content that is offensive, hateful, harassing, threatening, sexual, violent, misleading, spam, or that violates anyone's rights or the law. You can report any review or post in the app or by emailing info@pawground.app, and you can hide posts from any user. We review reports within 24 hours and will remove content that breaks these rules and may suspend or remove the accounts that posted it.` },
      { h2: `Private parks` },
      { p: `Private park listings are offered by independent hosts. Pawground is not responsible for host properties, guest behavior, or any injury, damage or dispute related to a visit.` },
      { h2: `Ending your account` },
      { p: `You can delete your account at any time in the app (Profile > Delete account) or by emailing info@pawground.app. We may suspend or remove accounts that break these terms.` },
      { h2: `Disclaimers and limits` },
      { p: `Pawground is provided "as is" without warranties of any kind. To the fullest extent allowed by law, Pawground is not liable for indirect or consequential damages, or for anything that happens at places listed on Pawground.` },
      { h2: `Changes` },
      { p: `We may update these terms. We will change the effective date above. If you keep using Pawground after changes, you accept the new terms.` },
      { h2: `Governing law` },
      { p: `These terms are governed by the laws of the State of Florida, USA.` },
      { h2: `Contact` },
      { p: `info@pawground.app` },
    ],
  },
};
